import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Send, Paperclip, Loader2, User, 
  FileText, Check, Image, AlertCircle, CheckCircle2, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import VoiceRecorder from '../components/VoiceRecorder';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AI_AVATAR = "/cloudgm-ai-orb.png";

// Lightweight markdown: **bold** + bullet lists, no external deps
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
};

const MarkdownText = ({ content }) => {
  const lines = content.split('\n');
  return (
    <>
      {lines.map((line, i) => {
        const bullet = line.match(/^\s*[*•-]\s+(.*)/);
        if (bullet) {
          return (
            <span key={i} className="flex gap-2">
              <span className="text-[#1E9BD9] flex-shrink-0">•</span>
              <span>{renderInline(bullet[1])}</span>
            </span>
          );
        }
        return (
          <span key={i}>
            {renderInline(line)}
            {i < lines.length - 1 && <br />}
          </span>
        );
      })}
    </>
  );
};

export default function ServiceChat() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [service, setService] = useState(null);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [requestNumber, setRequestNumber] = useState(null);
  const [currentDocIndex, setCurrentDocIndex] = useState(0); // Track which document is being requested
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const serviceId = searchParams.get('service_id');
  const serviceName = searchParams.get('service_name');

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize chat session
  useEffect(() => {
    if (serviceId) {
      initializeChatSession();
    } else {
      toast.error(isAr ? 'لم يتم تحديد الخدمة' : 'Service not specified');
      navigate('/services/visa');
    }
  }, [serviceId]);

  const initializeChatSession = async () => {
    setInitializing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
        navigate('/login');
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/customer/chat-service/start`,
        { service_id: serviceId, language: language },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSessionId(response.data.session_id);
        setService(response.data.service);
        setRequiredDocuments(response.data.required_documents || []);
        
        // Show typing animation first, then add welcome message
        setLoading(true);
        
        setTimeout(() => {
          setLoading(false);
          setMessages([{
            role: 'assistant',
            content: response.data.welcome_message,
            timestamp: new Date().toISOString()
          }]);
        }, 1200); // 1.2 second typing delay
      }
    } catch (error) {
      console.error('Error initializing chat:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || (isAr ? 'خطأ في بدء المحادثة' : 'Error starting chat'));
        navigate('/services/visa');
      }
    } finally {
      setInitializing(false);
    }
  };

  const sendMessage = async (eOrText) => {
    // Support both form submit and direct call with a transcribed string
    let override = null;
    if (eOrText && typeof eOrText === 'string') {
      override = eOrText;
    } else if (eOrText && typeof eOrText.preventDefault === 'function') {
      eOrText.preventDefault();
    }
    const userMessage = (override != null ? override : inputMessage).trim();
    if (!userMessage || loading || !sessionId) return;
    setInputMessage('');
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/customer/chat-service/message`,
        { 
          session_id: sessionId, 
          message: userMessage,
          language: language 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.response,
          timestamp: new Date().toISOString()
        }]);

        if (response.data.request_submitted) {
          setRequestSubmitted(true);
          setRequestNumber(response.data.request_number);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(isAr ? 'خطأ في إرسال الرسالة' : 'Error sending message');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(isAr ? 'نوع الملف غير مدعوم. استخدم JPEG, PNG, WEBP أو PDF' : 'File type not supported. Use JPEG, PNG, WEBP or PDF');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(isAr ? 'حجم الملف يجب أن يكون أقل من 10 ميجابايت' : 'File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    
    // Determine the current document being requested
    const currentDoc = requiredDocuments[currentDocIndex] || requiredDocuments[0];
    const documentType = currentDoc?.id || currentDoc?.name_ar || 'general';
    
    // Add uploading message
    setMessages(prev => [...prev, {
      role: 'user',
      content: `📎 ${isAr ? 'جاري رفع' : 'Uploading'}: ${file.name}`,
      timestamp: new Date().toISOString(),
      isFile: true,
      fileName: file.name
    }]);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('document_type', documentType); // Dynamic based on current requested document
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/customer/chat-service/upload`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      if (response.data.success) {
        // Update document index based on backend response
        if (response.data.is_valid) {
          if (response.data.next_document) {
            // Find the index of the next document
            const nextDocIndex = requiredDocuments.findIndex(
              doc => doc.id === response.data.next_document.id
            );
            if (nextDocIndex !== -1) {
              setCurrentDocIndex(nextDocIndex);
            }
          } else if (response.data.all_documents_collected) {
            // All documents collected
            setCurrentDocIndex(requiredDocuments.length);
          }
        }
        
        // Add AI validation response with the full message from backend
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.message || (response.data.is_valid 
            ? (isAr ? '✅ تم استلام المستند والتحقق منه بنجاح!' : '✅ Document received and validated successfully!')
            : (isAr ? '⚠️ يرجى إعادة رفع المستند بجودة أفضل' : '⚠️ Please re-upload the document with better quality')),
          timestamp: new Date().toISOString(),
          validation: response.data.validation,
          nextDocument: response.data.next_document,
          allDocumentsCollected: response.data.all_documents_collected
        }]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(isAr ? 'خطأ في رفع الملف' : 'Error uploading file');
      
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: isAr ? '❌ حدث خطأ في رفع الملف. يرجى المحاولة مرة أخرى.' : '❌ Error uploading file. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setUploadingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const surface = isDarkMode ? 'rgba(20,20,20,0.75)' : 'rgba(255,255,255,0.8)';
  const aiBubbleBg = isDarkMode ? 'rgba(26,26,26,0.9)' : '#ffffff';

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-primary" data-testid="service-chat-loading">
        <div className="flex flex-col items-center gap-5">
          <img src={AI_AVATAR} alt="CloudGM AI" className="w-24 h-24 animate-float" style={{ filter: 'drop-shadow(0 0 30px rgba(30,155,217,0.6))' }} />
          <p className="text-theme-secondary text-sm tracking-widest">CLOUDGM AI</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-theme-primary text-theme-primary relative" dir={isAr ? 'rtl' : 'ltr'} data-testid="service-chat-page">
      {/* Ambient AI background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 start-1/4 w-[500px] h-[500px] rounded-full opacity-25"
          style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.45) 0%, transparent 65%)' }} />
        <div className="absolute bottom-0 end-0 w-[420px] h-[420px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.35) 0%, transparent 65%)' }} />
        <div className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)'} 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }} />
      </div>

      {/* Header */}
      <header className="relative z-20 flex-shrink-0 glass-effect border-b border-theme" data-testid="service-chat-header">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/services/visa')}
              className="p-2.5 rounded-full border border-theme text-theme-secondary hover:text-theme-primary hover:border-[#1E9BD9]/50 transition-colors"
              data-testid="back-to-services"
            >
              {isAr ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
            </button>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.18) 0%, transparent 70%)' }}>
                    <img src={AI_AVATAR} alt="CloudGM AI" className="w-10 h-10 object-contain"
                      style={{ filter: 'drop-shadow(0 0 12px rgba(30,155,217,0.55))' }} />
                  </div>
                  <span className="absolute inset-0 rounded-full border border-[#1E9BD9]/40 animate-ping" style={{ animationDuration: '2.5s' }}></span>
                  <div className="absolute -bottom-0.5 -start-0.5 w-3 h-3 bg-[#10B981] rounded-full border-2" style={{ borderColor: 'var(--bg-primary)' }}></div>
                </div>
                <div className="min-w-0">
                  <h1 className="text-sm font-bold truncate gradient-text" data-testid="service-chat-bot-name">CLOUDGM AI</h1>
                  <p className="text-xs truncate text-theme-secondary flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-pulse flex-shrink-0"></span>
                    {serviceName ? decodeURIComponent(serviceName) : (isAr ? 'خدمات التأشيرات' : 'Visa Services')}
                  </p>
                </div>
              </div>
            </div>

            {/* Required Documents Badge */}
            {requiredDocuments.length > 0 && (
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-amber-500/30 bg-amber-500/10 text-amber-500" data-testid="required-docs-badge">
                <FileText className="h-3.5 w-3.5" />
                <span>{requiredDocuments.length} {isAr ? 'مستندات' : 'docs'}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <main className="relative z-10 flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              style={{ animation: 'fadeSlideIn 0.35s ease-out forwards', opacity: 0 }}
              data-testid={`service-chat-msg-${message.role}-${index}`}
            >
              {/* Avatar */}
              {message.role === 'assistant' ? (
                <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.15) 0%, transparent 70%)' }}>
                  <img src={AI_AVATAR} alt="CloudGM AI" className="w-8 h-8 object-contain"
                    style={{ filter: 'drop-shadow(0 0 8px rgba(30,155,217,0.5))' }} />
                </div>
              ) : (
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] flex items-center justify-center shadow-[0_0_16px_rgba(30,155,217,0.35)]">
                  <User className="h-4 w-4 text-white" />
                </div>
              )}

              {/* Message Bubble */}
              <div className={`max-w-[85%] ${message.role === 'user' ? 'text-end' : ''}`}>
                <div className={`inline-block px-4 py-3 text-start ${
                  message.role === 'assistant'
                    ? 'rounded-2xl rounded-ts-md border border-theme text-theme-primary backdrop-blur-xl'
                    : 'rounded-2xl rounded-te-md text-white bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] shadow-[0_6px_24px_rgba(30,155,217,0.35)]'
                }`}
                style={message.role === 'assistant' ? { background: aiBubbleBg } : {}}>
                  <p className="text-sm leading-relaxed">
                    <MarkdownText content={message.content} />
                  </p>

                  {/* Document validation details */}
                  {message.validation && (
                    <div className="mt-3 pt-3 border-t border-theme">
                      <div className="flex items-center gap-2 text-xs">
                        {message.validation.is_clear && message.validation.is_correct_type ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-amber-500" />
                        )}
                        <span className="text-theme-secondary">
                          {message.validation.document_type_detected && (
                            <>{isAr ? 'نوع المستند: ' : 'Document type: '}{message.validation.document_type_detected}</>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Timestamp */}
                <p className={`text-[11px] mt-1.5 px-1 text-theme-secondary flex items-center gap-1 ${message.role === 'user' ? 'justify-end' : ''}`}>
                  {message.role === 'assistant' && <Sparkles className="h-3 w-3 text-[#1E9BD9]" />}
                  {new Date(message.timestamp).toLocaleTimeString(isAr ? 'ar-AE' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}

          {/* Thinking indicator */}
          {loading && (
            <div className="flex gap-3" style={{ animation: 'fadeSlideIn 0.3s ease-out forwards' }} data-testid="service-chat-typing">
              <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: 'radial-gradient(circle, rgba(30,155,217,0.15) 0%, transparent 70%)' }}>
                <img src={AI_AVATAR} alt="CloudGM AI" className="w-8 h-8 object-contain animate-pulse"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(30,155,217,0.6))' }} />
              </div>
              <div className="px-5 py-4 rounded-2xl rounded-ts-md border border-theme backdrop-blur-xl" style={{ background: aiBubbleBg }}>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-[#1E9BD9] rounded-full" style={{ animation: 'aiDot 1.2s ease-in-out infinite', boxShadow: '0 0 8px rgba(30,155,217,0.8)' }}></span>
                    <span className="w-2 h-2 bg-[#1E9BD9] rounded-full" style={{ animation: 'aiDot 1.2s ease-in-out 0.15s infinite', boxShadow: '0 0 8px rgba(30,155,217,0.8)' }}></span>
                    <span className="w-2 h-2 bg-[#1E9BD9] rounded-full" style={{ animation: 'aiDot 1.2s ease-in-out 0.3s infinite', boxShadow: '0 0 8px rgba(30,155,217,0.8)' }}></span>
                  </div>
                  <span className="text-sm text-theme-secondary">{isAr ? 'يفكّر...' : 'Thinking...'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Request Submitted Success */}
          {requestSubmitted && requestNumber && (
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-6 text-white shadow-[0_10px_40px_rgba(16,185,129,0.35)]" data-testid="request-submitted-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <Check className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">
                    {isAr ? 'تم تقديم طلبك بنجاح!' : 'Request Submitted Successfully!'}
                  </h3>
                  <p className="text-sm text-white/80">
                    {isAr ? 'رقم الطلب' : 'Request Number'}: <span className="font-mono font-bold" data-testid="request-number">{requestNumber}</span>
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full bg-white/20 hover:bg-white/30 backdrop-blur text-white rounded-full border border-white/30"
                data-testid="go-to-dashboard"
              >
                {isAr ? 'الذهاب للوحة التحكم' : 'Go to Dashboard'}
              </Button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input Area */}
      {!requestSubmitted && (
        <footer className="relative z-20 flex-shrink-0 glass-effect border-t border-theme">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <form onSubmit={sendMessage}>
              <div className={`flex items-center gap-2 rounded-full border px-2.5 py-1.5 transition-colors backdrop-blur-xl border-theme focus-within:border-[#1E9BD9]/60 focus-within:ring-2 focus-within:ring-[#1E9BD9]/20`}
                style={{ background: surface }} data-testid="service-chat-input-zone">
                {/* File Upload Button */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  data-testid="service-chat-file-input"
                />
                {!isVoiceRecording && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile || loading}
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-theme-secondary hover:text-[#1E9BD9] hover:bg-[#1E9BD9]/10 transition-colors disabled:opacity-40"
                    data-testid="upload-file-button"
                  >
                    {uploadingFile ? (
                      <Loader2 className="h-5 w-5 animate-spin text-[#1E9BD9]" />
                    ) : (
                      <Paperclip className="h-5 w-5" />
                    )}
                  </button>
                )}

                {/* Voice Recorder — expands to full-width bar while recording */}
                <VoiceRecorder
                  onTranscript={(text) => sendMessage(text)}
                  onRecordingChange={setIsVoiceRecording}
                  language={isAr ? 'ar' : 'en'}
                  disabled={loading || uploadingFile}
                  testId="service-chat-voice-btn"
                />

                {!isVoiceRecording && (
                  <>
                    <input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder={isAr ? 'اسأل CloudGM AI أي شيء...' : 'Ask CloudGM AI anything...'}
                      disabled={loading || uploadingFile}
                      className="flex-1 bg-transparent border-none outline-none text-sm text-theme-primary placeholder:text-theme-secondary px-2 py-2.5"
                      data-testid="chat-input"
                    />

                    <button
                      type="submit"
                      disabled={!inputMessage.trim() || loading || uploadingFile}
                      className="flex-shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] text-white flex items-center justify-center shadow-[0_4px_20px_rgba(30,155,217,0.45)] hover:scale-105 transition-transform disabled:opacity-40 disabled:scale-100"
                      data-testid="send-message-button"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Send className={`h-5 w-5 ${isAr ? '-scale-x-100' : ''}`} />
                      )}
                    </button>
                  </>
                )}
              </div>
            </form>

            {/* Quick Tips */}
            <div className="mt-2 flex items-center justify-center gap-2 text-xs text-theme-secondary">
              <Sparkles className="h-3 w-3 text-[#1E9BD9]" />
              <span>CLOUDGM AI — {isAr ? 'يمكنك إرفاق مستندات أو التحدث بالصوت' : 'You can attach documents or speak by voice'}</span>
            </div>
          </div>
        </footer>
      )}

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes aiDot {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
